import { useTranslation } from 'react-i18next';

import { languageChoiceOf } from '../../../i18n/languages';
import type { LanguageChoice } from '../../../i18n/languages';

import { useSaveLanguage } from './useSaveLanguage';

export type LanguageTiming = 'instant' | 'afterSave';

export type LanguageStatus = 'idle' | 'saving' | 'paused' | 'saved' | 'failed';

export interface LanguageChoiceLogic {
  readonly shown: LanguageChoice;
  readonly status: LanguageStatus;
  readonly saved: LanguageChoice | null;
  readonly choose: (next: LanguageChoice, onChosen?: () => void) => void;
}

export interface LanguageChoiceOptions {
  readonly current: LanguageChoice;
  readonly timing: LanguageTiming;
}

const statusOf = (save: { readonly isPending: boolean; readonly isPaused: boolean; readonly isSuccess: boolean; readonly isError: boolean }): LanguageStatus => {
  if (save.isPaused) return 'paused';
  if (save.isPending) return 'saving';
  if (save.isSuccess) return 'saved';
  return save.isError ? 'failed' : 'idle';
};

export const useLanguageChoice = ({ current, timing }: LanguageChoiceOptions): LanguageChoiceLogic => {
  const { i18n } = useTranslation();
  const { mutate, isPending, isPaused, isSuccess, isError, variables } = useSaveLanguage();
  const picked = isPending || isSuccess ? variables : undefined;

  return {
    shown: picked ?? current,
    status: statusOf({ isPending, isPaused, isSuccess, isError }),
    saved: isSuccess ? variables ?? null : null,
    choose: (next, onChosen) => {
      if (isPending) return;
      const previous = languageChoiceOf(i18n.language);
      const instant = timing === 'instant' && next !== previous;
      if (instant) void i18n.changeLanguage(next);
      mutate(next, {
        onSuccess: () => onChosen?.(),
        onError: () => {
          if (instant) void i18n.changeLanguage(previous);
        },
      });
    },
  };
};
